import type { ReactNode } from 'react';
import { Card, Indicator, Stepper } from '@mantine/core';
import { Check } from 'react-feather';
import { SETUP_STEPS, type SetupIssue, type SetupStep } from '../model/steps';

interface MatchingStepperProps {
  step: SetupStep;
  issues: SetupIssue[];
  onStepChange: (step: SetupStep) => void;
}

/**
 * The 3-step stepper. Any step can be opened directly; steps with issues
 * carry a red count so the admin can see where to look.
 */
export function MatchingStepper({
  step,
  issues,
  onStepChange,
}: MatchingStepperProps) {
  const active = SETUP_STEPS.findIndex(s => s.id === step);
  return (
    <Card
      withBorder
      px="xl"
      py="md"
      mb="lg"
      component="nav"
      aria-label="Matching steps"
    >
      <Stepper
        active={active}
        onStepClick={i => onStepChange(SETUP_STEPS[i].id)}
        size="sm"
      >
        {SETUP_STEPS.map((s, i) => {
          const count =
            s.id === 'review'
              ? 0
              : issues.filter(x => x.target.step === s.id).length;
          // A finished step with issues still shows its count, so the badge
          // goes on both the normal and the completed icon.
          const badge = (content: ReactNode) =>
            count > 0 ? (
              <Indicator
                label={count}
                size={18}
                color="dangerRed"
                offset={-4}
                aria-label={`${count} issues to fix`}
              >
                {content}
              </Indicator>
            ) : undefined;
          return (
            <Stepper.Step
              key={s.id}
              label={s.label}
              icon={badge(i + 1)}
              completedIcon={badge(<Check size={16} />)}
            />
          );
        })}
      </Stepper>
    </Card>
  );
}
